import { ScrollArea as RadixScrollArea } from 'radix-ui';
import { forwardRef, type ComponentPropsWithoutRef, type ElementRef } from 'react';

import { cn } from '@/utils/cn';

/**
 * The scroll container used inside dialogs and long panels.
 *
 * A styled scrollbar rather than the platform's, for the same reason nothing else
 * here is native: a church's console should not look different on Windows than it
 * does on a Mac.
 *
 * `max-height` on the root is enough to constrain — the viewport inherits it. Relying
 * on `h-full` alone fails when the parent only has a max-height (common in dialogs),
 * which is why long form dialogs appeared not to scroll.
 */
export const ScrollArea = forwardRef<
  ElementRef<typeof RadixScrollArea.Root>,
  ComponentPropsWithoutRef<typeof RadixScrollArea.Root> & {
    orientation?: 'vertical' | 'horizontal' | 'both';
    viewportClassName?: string;
  }
>(({ className, children, orientation = 'vertical', viewportClassName, ...props }, ref) => (
  <RadixScrollArea.Root
    ref={ref}
    type="hover"
    className={cn('relative min-h-0 overflow-hidden', className)}
    {...props}
  >
    <RadixScrollArea.Viewport
      className={cn(
        'h-full max-h-[inherit] w-full overscroll-contain [&>div]:!block',
        viewportClassName,
      )}
    >
      {children}
    </RadixScrollArea.Viewport>

    {orientation !== 'horizontal' ? <Bar orientation="vertical" /> : null}
    {orientation !== 'vertical' ? <Bar orientation="horizontal" /> : null}
    <RadixScrollArea.Corner />
  </RadixScrollArea.Root>
));

ScrollArea.displayName = 'ScrollArea';

const Bar = ({ orientation }: { orientation: 'vertical' | 'horizontal' }) => (
  <RadixScrollArea.Scrollbar
    orientation={orientation}
    className={cn(
      'flex touch-none select-none p-0.5 transition-colors',
      orientation === 'vertical' ? 'w-2.5' : 'h-2.5 flex-col',
    )}
  >
    <RadixScrollArea.Thumb className="relative flex-1 rounded-full bg-border-strong" />
  </RadixScrollArea.Scrollbar>
);
